import { Image } from 'antd';
import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';
import { ifProp } from 'styled-tools';
import { FormTheme, FormThemeImageType } from 'api/form/types';
import { useFormTemplateUpdateMutation } from 'queries/form-template';
import {
  DefaultHeaderImagesList,
  IMAGE_HEIGHT,
  IMAGE_WIDTH,
} from './constants';
import { CustomImage } from './custom-image';
import { TickIcon } from './theme-components';

interface HeaderImageProps {
  formTheme: FormTheme;
  templateId: number;
}

const Wrapper = styled.div`
  padding: var(--layout-02) var(--layout-01);
  border-bottom: 1px solid var(--border-color);
`;
const ImagesListWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-03);
  margin-top: var(--spacing-04);
`;
const ImageItem = styled.div<{ isSelected?: boolean }>`
  position: relative;
  width: ${IMAGE_WIDTH}px;
  height: ${IMAGE_HEIGHT + 4}px;
  display: flex;
  border: 2px solid transparent;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  &:hover {
    box-shadow: var(--shadow-08);
    border-color: var(--blue-60);
  }
  ${ifProp(
    'isSelected',
    css`
      border-color: var(--blue-60);
      img {
        filter: brightness(80%);
      }
    `,
  )};
`;

const CustomImageWrapper = styled.div`
  margin-top: var(--spacing-06);
`;
const ThemeTickIcon = styled.div`
  position: absolute;
  top: 4px;
  left: 4px;
  z-index: var(--zindex-layer-01);
`;

const ImageNoneItem = styled(ImageItem)<{ isSelected?: boolean }>`
  border: 1px solid var(--border-color);
  ${ifProp(
    'isSelected',
    css`
      border: 2px solid var(--blue-60);
    `,
  )};
`;

export const HeaderImages: FC<HeaderImageProps> = ({
  templateId,
  formTheme,
}) => {
  const { mutate } = useFormTemplateUpdateMutation(templateId);

  const onImageUpdate = (imageUrl: string) => {
    mutate({
      formTheme: {
        ...formTheme,
        headerImage: imageUrl,
      },
    });
  };

  return (
    <Wrapper>
      <FormattedMessage id="form_templates.header_image" />

      <ImagesListWrapper>
        <ImageNoneItem
          isSelected={!formTheme.headerImage}
          onClick={() => onImageUpdate('')}
        >
          {!formTheme.headerImage ? (
            <ThemeTickIcon>
              <TickIcon />
            </ThemeTickIcon>
          ) : null}
          <FormattedMessage id="none" />
        </ImageNoneItem>
        {DefaultHeaderImagesList.map((image) => (
          <ImageItem
            isSelected={formTheme.headerImage === image.location}
            onClick={() => onImageUpdate(image.location)}
          >
            {formTheme.headerImage === image.location && (
              <ThemeTickIcon>
                <TickIcon />
              </ThemeTickIcon>
            )}
            <Image
              preview={false}
              src={image.thumbnailLocation}
              width={IMAGE_WIDTH}
              loading="lazy"
              height={IMAGE_HEIGHT}
            />
          </ImageItem>
        ))}
      </ImagesListWrapper>
      <CustomImageWrapper>
        <FormattedMessage id="picker.custom" />
        <CustomImage
          customImageLocation={formTheme.lastUploadedHeaderImage}
          imageType={FormThemeImageType.HeaderImage}
          templateId={templateId}
          isSelected={
            formTheme.headerImage === formTheme.lastUploadedHeaderImage
          }
          setImage={onImageUpdate}
        />
      </CustomImageWrapper>
    </Wrapper>
  );
};
